import { isValidElement, useCallback, useEffect, useMemo, useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import AutoMarquee from '@/shared-components/text/components/AutoMarquee';
import NumberUtils from '@/utils/number.utils';

import { ArtworkOfferPrice } from '../../components/ArtworkOfferElements';
import useEditionStatus from '../hooks/useEditionStatus';

import {
  isValidResaleOption,
  RESALE_COMING_SOON_MESSAGE,
  RESALE_CONTROLS_ENABLED,
  RESALE_OPTION,
} from './purchaseFooter.constants';
import {
  ResaleOfferPopupContent,
  ResaleOptionsPopupContent,
} from './ResalePopupContent';

import './styles/PurchaseFooterDesktop.css';

const DEFAULT_RESALE_CONTEXT = Object.freeze({
  active: false,
  isOwnerView: false,
  option: RESALE_OPTION.NONE,
  saving: false,
  lastSalePrice: null,
  lastSalePriceFormatted: null,
  lastSaleCurrency: '',
  onOptionChange: null,
  onOfferSubmit: null,
  isLimitedEdition: false,
  limitedEdition: null,
});

function toCurrencyCode(value, fallback = '') {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 3) {
      return trimmed.toUpperCase();
    }
    if (trimmed.length > 0) {
      return trimmed.toUpperCase();
    }
  }
  return fallback;
}

function parseNumeric(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(numeric) ? numeric : null;
}

function formatPriceDisplay(value, currency) {
  const numeric = parseNumeric(value);
  const currencyCode = toCurrencyCode(currency, 'EUR');

  if (numeric !== null) {
    if (Number.isInteger(numeric)) {
      try {
        return NumberUtils.getArtworkPrice(numeric, currencyCode);
      } catch (error) {
        // fallback a Intl si el util no puede con el importe
      }
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: numeric % 1 === 0 ? 0 : 2,
      }).format(numeric);
    } catch (error) {
      return `${numeric.toLocaleString()} ${currencyCode}`.trim();
    }
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  return 'â€”';
}

export default function PurchaseFooterDesktop({
  amountValue,
  amountLabel,
  centerAmount = false,
  buttonLabel,
  onButtonClick,
  disabled = false,
  loading = false,
  auxiliaryContent = null,
  offerPriceDetails = null,
  // NUEVO de add/auctions:
  label = null,
  variant = 'default',
  showFreeShipping = false,
  // De main-evil (reventa/ediciones):
  resaleContext = DEFAULT_RESALE_CONTEXT,
}) {
  const t = useTranslations('common.shared.artwork_view.purchase_footer');
  const { state } = useStateHandler();
  const [showPurchaseBlockedPopup, setShowPurchaseBlockedPopup] = useState(false);
  const isGalleryMember = state.user_session?.user_type !== "gallery"
    && Boolean(state.user_session?.gallery_id);

  // --- context reventa/ediciones (main-evil) ---
  const context = useMemo(
    () => (resaleContext && typeof resaleContext === 'object'
      ? { ...DEFAULT_RESALE_CONTEXT, ...resaleContext }
      : DEFAULT_RESALE_CONTEXT),
    [resaleContext],
  );

  const {
    active: resaleActive = false,
    isOwnerView: resaleOwnerView = false,
    option: resaleOptionIncoming = RESALE_OPTION.NONE,
    saving: resaleSaving = false,
    lastSalePrice: resaleLastSalePrice = null,
    lastSalePriceFormatted: contextLastSalePriceFormatted = null,
    lastSaleCurrency: contextLastSaleCurrency = '',
    onOptionChange,
    onOfferSubmit,
    isLimitedEdition: contextIsLimitedEdition = false,
    limitedEdition: limitedEditionMeta = null,
  } = context;

  const normalizedResaleOption = useMemo(
    () => (isValidResaleOption(resaleOptionIncoming) ? resaleOptionIncoming : RESALE_OPTION.NONE),
    [resaleOptionIncoming],
  );
  const resolvedAmountLabel = amountLabel ?? t('default_amount_label');
  const resolvedButtonLabel = buttonLabel ?? t('default_button_label');

  const limitedEditionProductId = limitedEditionMeta?.productId ?? null;
  const {
    data: editionStatusData,
    loading: editionStatusLoading,
  } = useEditionStatus(limitedEditionProductId, {
    enabled: contextIsLimitedEdition && Boolean(limitedEditionProductId),
  });

  const editionSoldOut = editionStatusData?.soldOut === true;
  const viewerOwnsLimitedEdition = editionStatusData?.viewerOwnsOne === true;

  const effectiveLastSaleCurrency = useMemo(() => {
    if (contextIsLimitedEdition) {
      return toCurrencyCode(editionStatusData?.currency ?? contextLastSaleCurrency, 'EUR');
    }
    return toCurrencyCode(contextLastSaleCurrency, '');
  }, [contextIsLimitedEdition, contextLastSaleCurrency, editionStatusData?.currency]);

  const effectiveLastSalePriceNumeric = useMemo(() => {
    if (contextIsLimitedEdition) {
      const statusPrice = parseNumeric(editionStatusData?.lastPurchasePrice);
      if (statusPrice !== null) {
        return statusPrice;
      }
    }
    const fallback = parseNumeric(resaleLastSalePrice);
    return fallback;
  }, [contextIsLimitedEdition, editionStatusData?.lastPurchasePrice, resaleLastSalePrice]);

  const effectiveLastSalePriceFormatted = useMemo(() => {
    if (contextIsLimitedEdition) {
      return formatPriceDisplay(
        editionStatusData?.lastPurchasePrice ?? resaleLastSalePrice,
        effectiveLastSaleCurrency,
      );
    }

    return contextLastSalePriceFormatted
      ?? formatPriceDisplay(resaleLastSalePrice, effectiveLastSaleCurrency);
  }, [
    contextIsLimitedEdition,
    contextLastSalePriceFormatted,
    editionStatusData?.lastPurchasePrice,
    effectiveLastSaleCurrency,
    resaleLastSalePrice,
  ]);

  const displayCurrency = useMemo(
    () => (effectiveLastSaleCurrency ? effectiveLastSaleCurrency.toUpperCase() : ''),
    [effectiveLastSaleCurrency],
  );

  const computedAmountValue = useMemo(() => {
    if (isValidElement(amountValue)) return amountValue;
    if (typeof amountValue === 'string' && amountValue.trim().length > 0 && amountValue.trim() !== 'â€”') {
      return amountValue;
    }

    if (!contextIsLimitedEdition) {
      return amountValue;
    }

    if (editionStatusLoading) {
      return 'â€”';
    }

    const candidatePrice = editionStatusData?.lastPurchasePrice ?? resaleLastSalePrice ?? amountValue;
    return formatPriceDisplay(candidatePrice, effectiveLastSaleCurrency);
  }, [
    amountValue,
    contextIsLimitedEdition,
    editionStatusData?.lastPurchasePrice,
    editionStatusLoading,
    effectiveLastSaleCurrency,
    resaleLastSalePrice,
  ]);

  const visitorLimitedEditionGatePass = !contextIsLimitedEdition
    || (!editionStatusLoading && editionSoldOut && viewerOwnsLimitedEdition);

  const resaleControlsEnabled = RESALE_CONTROLS_ENABLED === true;

  const isOwnerExperience = resaleActive && resaleOwnerView;
  const ownerResaleComingSoon = isOwnerExperience && !resaleControlsEnabled;

  const isVisitorOfferExperience = resaleActive
    && !resaleOwnerView
    && normalizedResaleOption !== RESALE_OPTION.NONE
    && visitorLimitedEditionGatePass
    && resaleControlsEnabled;

  const shouldRenderVisitorButton = !resaleOwnerView
    && normalizedResaleOption !== RESALE_OPTION.NONE
    && visitorLimitedEditionGatePass
    && resaleControlsEnabled;

  const hasButtonLabel = typeof resolvedButtonLabel === 'string' && resolvedButtonLabel.trim().length > 0;
  const shouldRenderPrimaryButton = hasButtonLabel
    && (!resaleActive || isOwnerExperience || shouldRenderVisitorButton);

  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(normalizedResaleOption);
  const [resaleError, setResaleError] = useState('');

  useEffect(() => {
    if (!optionsOpen) {
      setSelectedOption(normalizedResaleOption);
      setResaleError('');
    }
  }, [normalizedResaleOption, optionsOpen]);

  useEffect(() => {
    if (!isOwnerExperience) {
      setOptionsOpen(false);
    }
  }, [isOwnerExperience]);

  const openOptionsPopup = useCallback(() => setOptionsOpen(true), []);
  const closeOptionsPopup = useCallback(() => {
    if (resaleSaving) return;
    setOptionsOpen(false);
  }, [resaleSaving]);

  const handleSaveOptions = useCallback(async () => {
    if (!isOwnerExperience) {
      setOptionsOpen(false);
      return;
    }
    if (!onOptionChange || selectedOption === normalizedResaleOption) {
      setOptionsOpen(false);
      return;
    }
    try {
      setResaleError('');
      await onOptionChange(selectedOption);
      setOptionsOpen(false);
    } catch (error) {
      const message = error?.message ?? t('errors.save_preference');
      setResaleError(message);
    }
  }, [isOwnerExperience, normalizedResaleOption, onOptionChange, selectedOption, t]);

  const [offerOpen, setOfferOpen] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerError, setOfferError] = useState('');
  const [offerSubmitting, setOfferSubmitting] = useState(false);

  const openOfferPopup = useCallback(() => setOfferOpen(true), []);
  const closeOfferPopup = useCallback(() => {
    if (offerSubmitting) return;
    setOfferOpen(false);
    setOfferError('');
    setOfferAmount('');
  }, [offerSubmitting]);

  const handleOfferAmountChange = useCallback((event) => {
    setOfferAmount(event.target.value);
    if (offerError) setOfferError('');
  }, [offerError]);

  const enforceHigherOffer = normalizedResaleOption === RESALE_OPTION.HIGHER;

  const handleConfirmOffer = useCallback(async () => {
    const parsedAmount = Number(offerAmount);

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setOfferError(t('errors.enter_valid_offer'));
      return;
    }

    if (
      enforceHigherOffer
      && effectiveLastSalePriceNumeric !== null
      && parsedAmount <= effectiveLastSalePriceNumeric
    ) {
      const thresholdLabel = effectiveLastSalePriceFormatted
        ?? `${effectiveLastSalePriceNumeric.toLocaleString()} ${displayCurrency}`.trim();
      setOfferError(`Offer must be higher than ${thresholdLabel}.`);
      return;
    }

    if (offerSubmitting) return;

    setOfferError('');
    setOfferSubmitting(true);

    try {
      if (typeof onOfferSubmit === 'function') {
        await onOfferSubmit(parsedAmount);
      }
      closeOfferPopup();
    } catch (error) {
      setOfferError(error?.message ?? t('errors.submit_offer'));
    } finally {
      setOfferSubmitting(false);
    }
  }, [
    closeOfferPopup,
    displayCurrency,
    enforceHigherOffer,
    effectiveLastSalePriceFormatted,
    effectiveLastSalePriceNumeric,
    offerAmount,
    offerSubmitting,
    onOfferSubmit,
    t,
  ]);

  useEffect(() => {
    if (!isVisitorOfferExperience) {
      setOfferOpen(false);
      setOfferError('');
      setOfferAmount('');
    }
  }, [isVisitorOfferExperience]);

  const primaryButtonDisabled = ownerResaleComingSoon
    || (isOwnerExperience ? resaleSaving : (disabled || loading))
    || (isVisitorOfferExperience && offerSubmitting);

  const handlePrimaryButtonClick = useCallback(() => {
    if (isGalleryMember) {
      setShowPurchaseBlockedPopup(true);
      return;
    }
    if (ownerResaleComingSoon) {
      return;
    }
    if (primaryButtonDisabled) return;

    if (isOwnerExperience) {
      openOptionsPopup();
      return;
    }

    if (isVisitorOfferExperience) {
      openOfferPopup();
      return;
    }

    if (typeof onButtonClick === 'function') {
      onButtonClick();
    }
  }, [
    isGalleryMember,
    ownerResaleComingSoon,
    isOwnerExperience,
    isVisitorOfferExperience,
    onButtonClick,
    openOfferPopup,
    openOptionsPopup,
    primaryButtonDisabled,
  ]);

  const primaryButtonText = loading ? t('processing') : resolvedButtonLabel;

  // --- NUEVO: variant wrapper (add/auctions) ---
  const rootClasses = [
    'purchase-footer__desktop',
    variant ? `purchase-footer__desktop--${variant}` : '',
  ].filter(Boolean).join(' ');

  const hasAmountLabel = typeof resolvedAmountLabel === 'string' && resolvedAmountLabel.trim().length > 0;

  const primaryButtonElement = resaleActive ? (
    <ContinueButton
      onClick={handlePrimaryButtonClick}
      className="purchase-footer__desktop__continue"
      loading={isOwnerExperience ? resaleSaving : false}
      turnOff={primaryButtonDisabled}
      continueClass={false}
    >
      {primaryButtonText}
    </ContinueButton>
  ) : (
    <button
      type="button"
      className="purchase-footer__desktop__button"
      onClick={handlePrimaryButtonClick}
      disabled={primaryButtonDisabled}
    >
      {primaryButtonText}
    </button>
  );

  return (
    <>
      <div className={rootClasses}>
        <div className="purchase-footer__desktop__info">
          {/* NUEVO badge/label de auctions (si lo usas, aÃ±ade su CSS) */}
          {label ? (
            <span className="purchase-footer__desktop__badge">{label}</span>
          ) : null}

          <div className={`purchase-footer__desktop__amount-stack${centerAmount ? ' purchase-footer__desktop__amount-stack--centered' : ''}`}>
            {hasAmountLabel && (
              <span className="purchase-footer__desktop__amount-label">{resolvedAmountLabel}</span>
            )}
            <span className="purchase-footer__desktop__value">
              {offerPriceDetails?.offerPrice !== null && offerPriceDetails?.offerPrice !== undefined ? (
                <ArtworkOfferPrice
                  originalPrice={offerPriceDetails.originalPrice}
                  offerPrice={offerPriceDetails.offerPrice}
                  currency={offerPriceDetails.currency}
                />
              ) : (
                computedAmountValue
              )}
            </span>
          </div>

          {showFreeShipping && (
            <AutoMarquee
              className="purchase-footer__desktop__free-shipping-marquee"
              contentClassName="purchase-footer__desktop__free-shipping-track"
              dependencies={[t('free_shipping')]}
            >
              <span className="purchase-footer__desktop__free-shipping">
                {t('free_shipping')}
              </span>
            </AutoMarquee>
          )}

          {auxiliaryContent}

        </div>

        {shouldRenderPrimaryButton && (
          <div className="purchase-footer__desktop__actions">
            {ownerResaleComingSoon && (
              <span className="purchase-footer__desktop__coming-soon">
                {RESALE_COMING_SOON_MESSAGE}
              </span>
            )}
            {primaryButtonElement}
          </div>
        )}
      </div>

      {isOwnerExperience && (
        <GenericPopup
          open={optionsOpen}
          className="purchase-footer__options-popup remove-scrollbar"
          opacity
          onBackgroundClick={closeOptionsPopup}
        >
          <ResaleOptionsPopupContent
            selectedOption={selectedOption}
            onOptionChange={setSelectedOption}
            onSubmit={handleSaveOptions}
            onClose={closeOptionsPopup}
            error={resaleError}
            saving={resaleSaving}
          />
        </GenericPopup>
      )}

      {isVisitorOfferExperience && (
        <GenericPopup
          open={offerOpen}
          className="purchase-footer__offer-popup remove-scrollbar"
          opacity
          onBackgroundClick={closeOfferPopup}
        >
          <ResaleOfferPopupContent
            displayCurrency={displayCurrency}
            enforceHigherOffer={enforceHigherOffer}
            effectiveLastSalePriceFormatted={effectiveLastSalePriceFormatted}
            offerAmount={offerAmount}
            onAmountChange={handleOfferAmountChange}
            onSubmit={handleConfirmOffer}
            onClose={closeOfferPopup}
            submitting={offerSubmitting}
            error={offerError}
          />
        </GenericPopup>
      )}

      <ActionRejectedPopup
        open={showPurchaseBlockedPopup}
        close={() => setShowPurchaseBlockedPopup(false)}
        description={t('errors.gallery_member_purchase_blocked')}
      />
    </>
  );
}
